/**
 *
 * @module exceptionReference
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/Error-Handling.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/Error-Handling.md#error-cases
 *
 */

/**
 *
 * @access public
 * @typedef SDK.Services.Exception.ExceptionReference
 * @type {Object}
 * @since 3.7.0
 * @desc Internal Exception reference.
 *
 * @property {Object} account
 * @property {Object} bamIdentity
 * @property {Object} commerce
 * @property {Object} configuration
 * @property {Object} common
 * @property {Object} global
 * @property {Object} media
 * @property {Object} purchase
 * @property {Object} userProfile
 *
 */
export default {
    account: {
        accountNotFound: {
            name: 'AccountNotFoundException',
            message: 'The account was not found'
        },
        attributeValidation: {
            name: 'AttributeValidationException',
            message: 'One or more attribute was invalid.'
        },
        invalidToken: {
            name: 'InvalidTokenException',
            message: 'External identity token was invalid.'
        },
        linkSubscriptionPartial: {
            name: 'LinkSubscriptionPartialException',
            message: 'Copy partial Error'
        }
    },
    bamIdentity: {
        attributeUpdateFailed: {
            name: 'AttributeUpdateFailedException',
            message: 'The attributes were not updated due to required field removal or failed validation.'
        },
        authenticationRequired: {
            name: 'AuthenticationRequiredException',
            message: 'User has not authenticated recently and must log in again.'
        },
        invalidCredentials: {
            name: 'InvalidCredentialsException',
            message: 'Incorrect email or password.'
        },
        invalidEmail: {
            name: 'InvalidEmailException',
            message: 'The new email address is invalid'
        },
        invalidEmailSyntax: {
            name: 'InvalidEmailSyntaxException',
            message: 'Invalid email address syntax'
        },
        invalidPasscode: {
            name: 'InvalidPasscodeException',
            message: 'The passcode is invalid'
        },
        rateLimited: {
            name: 'RateLimitedException',
            message: 'Too many passcodes have been requested.'
        },
        tokenAlreadyUsed: {
            name: 'TokenAlreadyUsedException',
            message: 'Unable to use the reset token, it has already been used.'
        },
        tokenInvalid: {
            name: 'TokenInvalidException',
            message: 'Unable to reset the password as the reset token is not recognized by the service.'
        },
        updateIdentityConflict: {
            name: 'UpdateIdentityConflictException',
            message: 'The new email address is already taken.'
        }
    },
    commerce: {
        invalidData: {
            name: 'InvalidDataException',
            message: 'Unable to create payment information due to invalid data.'
        },
        invalidZipCode: {
            name: 'InvalidZipCodeException',
            message: 'Invalid or nonexistant zip code.'
        },
        invalidPaymentInformation: {
            name: 'InvalidPaymentInformationException',
            message: 'Unable to submit the order due to invalid payment information.'
        },
        paymentMethodNotFound: {
            name: 'PaymentMethodNotFoundException',
            message: 'Unable to retrieve the payment method as it was not found.'
        }
    },
    common: {
        authenticationExpired: {
            name: 'AuthenticationExpiredException',
            message: 'Authentication has expired and needs to be updated before proceeding.'
        },
        invalidState: {
            name: 'InvalidStateException',
            message: 'The operation being attempted is not valid for the current state.'
        },
        locationAcquisition: {
            name: 'LocationAcquisitionException',
            message: 'Unable to get user location due to location service being disabled.'
        },
        locationNotAllowed: {
            name: 'LocationNotAllowedException',
            message: 'User is not allowed to use the app in their current location.'
        },
        network: {
            name: 'NetworkException',
            message: 'Problem with the underlying network connection.'
        },
        temporarilyUnavailable: {
            name: 'TemporarilyUnavailableException',
            message: 'There was a general error while processing the request'
        }
    },
    configuration: {
        configurationNotFound: {
            name: 'ConfigurationNotFoundException',
            message: 'Unable to locate configuration data based on the supplied bootstrap parameter.'
        }
    },
    content: {
        invalidRequest: {
            name: 'InvalidRequestException',
            message: 'The content query request was invalid'
        }
    },
    media: {
        authorizationExpired: {
            name: 'AuthorizationExpiredException',
            message: 'Could not reauthorize before attempting to access media.'
        },
        blackout: {
            name: 'BlackoutException',
            message: 'The current user is not allowed to access the media for the requested license because they are in a blackout location.' // eslint-disable-line max-len
        },
        drm: {
            name: 'DrmException',
            message: 'General DRM error.'
        },
        keyNotFound: {
            name: 'KeyNotFoundException',
            message: 'The requested license is not available.'
        },
        loginRequired: {
            name: 'LoginRequiredException',
            message: 'The requested media can only be viewed after authentication.'
        },
        mediaNotAllowed: {
            name: 'MediaNotAllowedException',
            message: 'The current user is not allowed to access the media for the requested license.'
        },
        mediaUnavailable: {
            name: 'MediaUnavailableException',
            message: 'The requested media is not available.'
        },
        notEntitled: {
            name: 'NotEntitleException',
            message: 'The user is not entitled to the requested media for the requested license.'
        },
        offlineNotPermitted: {
            name: 'OfflineNotPermittedException',
            message: 'The current user is not allowed to obtain the requested license because the media is not available for offline viewing.' // eslint-disable-line max-len
        },
        parentalControls: {
            name: 'ParentalControlsException',
            message: 'The current user is not allowed to access the media for the requested license due to parental controls restrictions on the account.' // eslint-disable-line max-len
        },
        streamConcurrency: {
            name: 'StreamConcurrencyException',
            message: 'The current user is not allowed to access the media for the requested license because they have too many concurrent streams.' // eslint-disable-line max-len
        },
        thumbnailsNotAvailable: {
            name: 'ThumbnailsNotAvailableException',
            message: 'No thumbnails are available for the given media.'
        }
    },
    purchase: {
        activationFailed: {
            name: 'ActivationFailedException',
            message: 'The service failed to activate the receipt.'
        },
        retryFailed: {
            name: 'RetryFailedException',
            message: 'The SDK exhaused all retry attempts without successfully activating.'
        }
    },
    userProfile: {
        maximumProfilesReached: {
            name: 'MaximumProfilesReachedException',
            message: 'The maximum number of profiles for the user\'s account has already been reached.'
        },
        profileRetrieval: {
            name: 'ProfileRetrievalException',
            message: 'The profiles for the user\'s account could not be retrieved.'
        },
        userProfileDeleteFailed: {
            name: 'UserProfileDeleteFailedException',
            message: 'The userProfile failed to be deleted.'
        },
        userProfileNotFound: {
            name: 'UserProfileNotFoundException',
            message: 'The userProfile was not found'
        },
        userProfileUpdateFailed: {
            name: 'UserProfileUpdateFailedException',
            message: 'The userProfile update failed'
        }
    }
};



// WEBPACK FOOTER //
// ./src/services/exception/exceptionReference.js